import gsap, { Power3 } from 'gsap';
import { Group, MeshPhongMaterial, PMREMGenerator } from 'three';
import { RoomEnvironment } from 'three/addons/environments/RoomEnvironment.js';
import { Maths } from '../_app/cuchillo/utils/Maths';
import { isMobile } from '../_app/cuchillo/core/Basics';

export const PHONG_SETTINGS = {
  color: '#000000',
  shininess: 10,
};

import WebGLSketch from '../_app/cuchillo/3D/WebGLSketch';
import DepthImage, { DEPTH_SETTINGS } from './DepthImage';
import ThumbnailObject from './ThumbnailObject';
import { GetBy } from '../_app/cuchillo/core/Element';
import { Metrics } from '../_app/cuchillo/core/Metrics';
import { Scroll } from '../_app/cuchillo/scroll/Scroll';
import Scene from './Scene';
import DebugPane from './DebugPane';
import Box from './Box';
import Bottle from './Bottle';
import { Ease } from '../_app/cuchillo/utils/Ease';
// Llums exportades des de Scene.js — re-export per compatibilitat amb imports existents
export { PRODUCT_LIGHTS_OBSCURIOS, PRODUCT_LIGHTS_LUMINARIS } from './Scene';

const THUMB_SETTINGS = {
  introZ: 4000,
  introY: 1000
}

const GRID_INTRO_Z = 4000;
const GRID_EXIT_Z = 4000;
const GRID_EXIT_START = 0.5;
const GRID_EXIT_STAGGER = 0.05;

// Rotació del box relativa a la posició DINS el viewport (no a Scroll.y absolut → mai
// s'acumula fins a quedar d'esquena, sigui quin sigui l'aspect ratio). La cara del disseny
// de box-4.glb està desplaçada ~-30° al model → base a ~-29° perquè quedi frontal + una mica lateral.
const BOX_ROT_BASE = Math.PI * -0.08;   // ~-29° (frontal + una mica lateral, box centrat)
const BOX_ROT_SCROLL = Math.PI * 0.3;   // gir total mentre travessa el viewport (±~27°)

export default class ProductScene {
  static group = new Group();
  static items = [];
  static thumb = null;
  static _categ = null;
  static _tempGrids = [];
  static _tempContainers = [];
  static active = false;
  static _objEnv = null; // environment neutre (RoomEnvironment) per il·luminar els __obj (PBR incrustat)

  // Environment map neutre (estil visor glTF) generat un cop via PMREM des de RoomEnvironment.
  // S'aplica NOMÉS al material dels __obj (envMap) → els il·lumina com el preview de Drive sense
  // dependre de llums d'escena i sense afectar cap altre objecte (box/bottle/tapon/character).
  static _getObjEnv() {
    if (this._objEnv) return this._objEnv;
    const pmrem = new PMREMGenerator(WebGLSketch.renderer);
    this._objEnv = pmrem.fromScene(new RoomEnvironment(), 0.04).texture;
    pmrem.dispose();
    return this._objEnv;
  }

  static init(container) {
    if (!WebGLSketch.renderer) WebGLSketch.init();

    WebGLSketch.scene.add(this.group);
    const bottleEl = GetBy.class('__bottle', container)[0];
    const categ = bottleEl ? bottleEl.dataset.categ : null;
    this._categ = categ;

    // Llums de producte centralitzades a Scene (layer 2)
    Scene.initProductLights(categ);

    const thumbEls = [...GetBy.class('__thumbnail', container)]
      .filter(el => !el.closest('.block-more-products'));
    for (let i = 0; i < thumbEls.length; i++) {
      const thumb = new ThumbnailObject({
        dom: thumbEls[i],
        link: thumbEls[i].dataset.link || '',
        hasMouse: true,
        groupLerp: 0.06,
      });
      if (i === 0) {
        thumb._introZ = THUMB_SETTINGS.introZ;
        thumb._introY = THUMB_SETTINGS.introY;
        this.thumb = thumb;
      } else {
        this.items.push(thumb);
      }
      thumb.setOpacity(i === 0 ? 0 : 1);

      // Layer de la categoria del producte (mateix pass que box/bottle → oclusió correcta).
      thumb.init().then(() => Scene.setProductLayer(thumb, this._categ));
      this.group.add(thumb);
    }

    const objEl = GetBy.class('__obj', container)[0];
    const objModel = objEl.dataset.model;
    // __obj: carreguem el glb TAL QUAL, conservant el material + textura INCRUSTATS del model (com
    // el preview del visor glTF). Per això NO li passem material propi ni diffuse externa: el Box
    // només substitueix el material si li'n passem un. El material incrustat és PBR, així que
    // l'il·luminem amb un environment neutre (envMap del RoomEnvironment) aplicat NOMÉS al seu
    // material → no depèn de llums d'escena (Pass 4 no en té) i no afecta res més.
    const obj = new Box({ model: objModel, dom: objEl, rotFactor: .1, cols: .5 });
    this.items.push(obj);
    obj.init().then(() => {
      obj.active = true;
      Scene.setObjLayer(obj); // __obj → OBJ_LAYER (2), Pass 4
      const env = this._getObjEnv();
      obj.traverse(o => {
        if (o.isMesh && o.material && 'envMap' in o.material) {
          o.material.envMap = env;
          o.material.needsUpdate = true;
        }
      });
      this.group.add(obj);
      obj.resize();
    });

    const boxEl = GetBy.class('__box', container)[0];
    const boxModel = boxEl.dataset.model;
    const boxDiffusion = boxEl.dataset.diffusion;
    const boxSpecular = boxEl.dataset.specular;
    // Luminaris = objecte de TEXTURA BLANCA il·luminat amb llums FOSQUES (Pass L4): el color
    // ha de ser blanc (com fa ArtScene), si no el box queda negre. Obscurios manté el gris
    // base (llums blanques L5). La resta (shininess) des de PHONG_SETTINGS (debug pane).
    const material = new MeshPhongMaterial({
      color: categ === 'luminaris' ? 0xF1F1F1 : PHONG_SETTINGS.color,
      shininess: PHONG_SETTINGS.shininess,
    });
    const box = new Box({ model: boxModel, dom: boxEl, diffusion: boxDiffusion, specular: boxSpecular, material, hasDrag: true, hasFlotation: true, hasFrontPlane: true, rotFactor: .2, cols: 22 });
    this.items.push(box);
    box.init().then(() => {
      box.active = true;
      Scene.setProductLayer(box, categ);
      this.group.add(box);
      box.resize();
      DebugPane.addBox(box, PHONG_SETTINGS); // debug: color base + shininess + spin (bind directe a PHONG_SETTINGS)
    });

    const bottleModel = bottleEl.dataset.model;
    const color = categ === 'luminaris' ? 0xF1F1F1 : 0x000000;
    const bottleMaterial = new MeshPhongMaterial({ color });
    const bottle = new Bottle({ model: bottleModel, dom: bottleEl, material: bottleMaterial, hasDrag: true, hasFlotation: true, rotFactor: .2, cols: .5 });
    this.items.push(bottle);
    bottle.init().then(() => {
      bottle.active = true;
      Scene.setProductLayer(bottle, categ);
      this.group.add(bottle);
      bottle.resize();
    });

    this._tempGrids = [];
    this._tempContainers = [];
  }

  // Crea els thumbnails d'UN block "more products" concret, de forma diferida: abans es
  // cridava per a TOTS els blocks de cop dins static init() (a l'inici de la càrrega de la
  // pàgina) — cada thumbnail carrega el seu propi GLB+textura i un MeshPhysicalMaterial amb
  // transmission (car), tot competint amb els assets principals del producte just quan
  // carrega la pàgina, encara que la graella sigui molt més avall i l'usuari potser no hi
  // arribi mai a fer scroll. Ara ScrollItem__BlockMoreProducts la crida des del seu onShow,
  // just quan el block és a punt d'entrar en pantalla. Idempotent: si ja existeix, no la
  // torna a crear (ensureGrid la protegeix igualment, però es manté per seguretat).
  static _initGridFor(containerEl) {
    const items = [];
    const thumbEls = GetBy.class('__thumbnail', containerEl);
    for (let i = 0; i < thumbEls.length; i++) {
      const el = thumbEls[i];
      const img = new ThumbnailObject({
        dom: el,
        link: el.dataset.link || '',
        hasMouse: true, zFactor: 0.08,
      });
      img._introZ = GRID_INTRO_Z;
      img._exitZ = 0;
      img.setOpacity(0);

      // ThumbnailObject.init cablejat del clic (des de `link`) i textura; layer de la
      // categoria del producte quan la textura ha carregat.
      img.init().then(() => Scene.setProductLayer(img, this._categ));
      this.group.add(img);

      items.push(img);
    }
    this._tempContainers.push(containerEl);
    this._tempGrids.push(items);
    return this._tempContainers.length - 1;
  }

  // Retorna l'índex del grid d'aquest container, creant-lo ara (diferit) si encara no existeix.
  static ensureGrid(containerEl) {
    const existing = this._tempContainers.indexOf(containerEl);
    return existing >= 0 ? existing : this._initGridFor(containerEl);
  }

  static getGridIndex(el) {
    return this._tempContainers.indexOf(el);
  }

  static showTempGrid(gridIndex) {
    const items = this._tempGrids[gridIndex] || [];
    const stagger = isMobile ? 0.04 : 0.08;
    const duration = 2.5;
    items.forEach((img, i) => {
      gsap.killTweensOf(img);
      gsap.killTweensOf(img._op);
      gsap.to(img._op, { value: 1, duration: 0.5, delay: 0.2 + i * stagger });
      gsap.to(img, { _introZ: 0, duration, delay: i * stagger, ease: Power3.easeOut });
    });
  }

  static moveTempGrid(gridIndex, progress) {
    const items = this._tempGrids[gridIndex] || [];
    items.forEach((img, i) => {
      const start = GRID_EXIT_START + i * GRID_EXIT_STAGGER;
      img._exitZ = Maths.clamp(Maths.map(progress, start, 1, 0, 1), 0, 1) * GRID_EXIT_Z;
    });
  }

  static loop() {
    if (!this.active) return;

    this.items.forEach(item => {
      if (!item.mesh) return; // GLB encara no carregat (init async)
      const { x, y, width, height } = item.dom.getBoundingClientRect();
      if (width === 0 || height === 0) return;

      const pos = item.domPositionTo3D(x, y);
      item.pos.x = pos.x + width * 0.5
      item.pos.y = pos.y - height * 0.5;
      item.pos.z = item.dataZ;

      // if (item instanceof DepthImage) {
      //   item.material.uniforms.mouse.value.x = WebGLSketch.mouse.x;
      //   item.material.uniforms.mouse.value.y = WebGLSketch.mouse.y;
      //   item.material.uniforms.depthStrength.value = DEPTH_SETTINGS.depthStrength;
      //   item.material.uniforms.mouseStrength.value = DEPTH_SETTINGS.mouseStrength;
      // } else
      if (item instanceof Box) {
        // Gir relatiu a la posició del box dins el viewport (no a Scroll.y absolut).
        const boxCenterY = y + height * 0.5;
        const vp = (boxCenterY - Metrics.HEIGHT * 0.5) / Metrics.HEIGHT; // ~[-0.5,0.5], 0 = centrat
        item.rot.y = BOX_ROT_BASE + vp * BOX_ROT_SCROLL;
      } else if (item instanceof Bottle) {
        item.rot.y = Scroll.y * 0.0015;
      }

      item.update();
    });

    // Header Thumbnail (GLB async → posicionem només quan el mesh existeix)
    if (this.thumb && this.thumb.mesh) {
      const { x, y, width, height } = this.thumb.dom.getBoundingClientRect();
      if (width !== 0 && height !== 0) {
        const pos = this.thumb.domPositionTo3D(x, y);
        this.thumb.pos.x = pos.x + width * 0.5;
        this.thumb.pos.y = pos.y - height * 0.5 + (this.thumb._introY || 0) - THUMB_SETTINGS.introY * Scroll.y * 0.0004;
        this.thumb.pos.z = this.thumb.dataZ + (this.thumb._introZ || 0) + THUMB_SETTINGS.introZ * Scroll.y * 0.00015;
        this.thumb.update();
      }
    }

    this._tempGrids.forEach(items => {
      items.forEach(img => {
        if (!img.mesh) return; // GLB encara no carregat (init async)
        const { x, y, width, height } = img.dom.getBoundingClientRect();
        if (width === 0 || height === 0) return;
        const pos = img.domPositionTo3D(x, y);
        img.pos.x = pos.x + width * 0.5;
        img.pos.y = pos.y - height * 0.5;
        img.pos.z = img.dataZ + (img._introZ || 0) + (img._exitZ || 0);
        img.update();
      });
    });
  }

  static animateIn() {
    if (!this.thumb) return;

    this.active = true;

    gsap.to(this.thumb, { _introZ: 0, duration: 2.5, ease: Ease.EASE_CUCHILLO_OUT });
    gsap.to(this.thumb, { _introY: 0, duration: 2, ease: Ease.EASE_CUCHILLO_OUT });
    gsap.to(this.thumb._op, { value: 1, duration: .5, delay: 1, ease: 'power1.out' });
  }

  static hide() {
    this.active = false;

    if (this.thumb) {
      gsap.killTweensOf(this.thumb);
      gsap.killTweensOf(this.thumb._op);
    }
  }

  static resize() {
    this.thumb?.resize();
    this.items.forEach(item => item.resize());
    this._tempGrids.forEach(items => items.forEach(img => img.resize()));
    // Les llums les redimensiona Scene.resize()
  }

  static dispose() {
    DebugPane.removeBox();
    Scene.disposeProductLights();

    this.items.forEach(item => {
      gsap.killTweensOf(item);
      this.group.remove(item);
      item.dispose();
    });
    this.items = [];

    if (this.thumb) {
      gsap.killTweensOf(this.thumb);
      gsap.killTweensOf(this.thumb._op);
      this.group.remove(this.thumb);
      this.thumb.dispose();
      this.thumb = null;
    }

    this._tempGrids.forEach(items => {
      items.forEach(img => {
        gsap.killTweensOf(img);
        gsap.killTweensOf(img._op);
        this.group.remove(img);
        img.dispose();
      });
    });
    this._tempGrids = [];
    this._tempContainers = [];

    WebGLSketch.scene.remove(this.group);
  }
}
